import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    LayoutDashboard, Brain, Beaker, MapPin, Trophy, Users, BarChart3, Target,
    Globe, History, Shield, Activity, Menu, X, LogOut, ChevronDown, Zap, Star, Send, Radio, Sparkles, Database, Bell, Satellite
} from 'lucide-react';

const navSections = [
    {
        title: 'VIMAL', items: [
            { path: '/vimal-dashboard', icon: Star, label: 'Vimal Dashboard', highlight: true },
        ]
    },
    {
        title: 'CORE', items: [
            { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { path: '/ai-match-lab', icon: Brain, label: 'AI Match Lab' },
            { path: '/simulation', icon: Beaker, label: 'Simulation Lab' },
            { path: '/fantasy', icon: Sparkles, label: 'Fantasy XI', highlight: true },
        ]
    },
    {
        title: 'INTELLIGENCE', items: [
            { path: '/global-predictions', icon: Globe, label: 'Global Predictions' },
            { path: '/external-intelligence', icon: Satellite, label: 'External Intelligence', highlight: true },
            { path: '/intelligence-pipeline', icon: Database, label: 'Data Pipeline', highlight: true },
            { path: '/model-retraining', icon: Brain, label: 'Live Model Retraining', highlight: true },
            { path: '/live', icon: Zap, label: 'Live Match' },
            { path: '/venues', icon: MapPin, label: 'Venues' },
        ]
    },
    {
        title: 'DATA', items: [
            { path: '/standings', icon: Trophy, label: 'Standings' },
            { path: '/teams', icon: Users, label: 'Teams' },
            { path: '/players', icon: Users, label: 'Players' },
            { path: '/matchups', icon: BarChart3, label: 'Matchups' },
        ]
    },
    {
        title: 'ANALYTICS', items: [
            { path: '/pipeline-health', icon: Activity, label: 'Pipeline Health', highlight: true },
            { path: '/season-preview', icon: Trophy, label: 'Season Preview', highlight: true },
            { path: '/god-mode', icon: Activity, label: 'God Mode' },
            { path: '/model-performance', icon: Target, label: 'Model Accuracy' },
            { path: '/history', icon: History, label: 'Prediction History' },
        ]
    },
    {
        title: 'AUTOMATION', items: [
            { path: '/realtime', icon: Radio, label: 'Real-Time Command', highlight: true },
            { path: '/social-hub', icon: Send, label: 'Social Media Hub' },
            { path: '/notifications', icon: Bell, label: 'Notification Alerts', highlight: true },
        ]
    },
    {
        title: 'ADMIN', items: [
            { path: '/admin', icon: Shield, label: 'Admin Panel' },
        ]
    },
];

// Mobile bottom bar items (5 most important)
const mobileNav = [
    { path: '/vimal-dashboard', icon: Star, label: 'Vimal', highlight: true },
    { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    { path: '/ai-match-lab', icon: Brain, label: 'Predict' },
    { path: '/standings', icon: Trophy, label: 'Standings' },
    { path: '/players', icon: Users, label: 'Players' },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState({});
    const [unreadCount, setUnreadCount] = useState(0);

    const toggleSection = (title) => setCollapsed(p => ({ ...p, [title]: !p[title] }));

    // Poll notification count
    useEffect(() => {
        const fetchCount = () => {
            import('../lib/api').then(({ default: API }) => {
                API.get('/intelligence/notifications/count').then(r => setUnreadCount(r.data?.unread_count || 0)).catch(() => {});
            });
        };
        fetchCount();
        const iv = setInterval(fetchCount, 30000);
        return () => clearInterval(iv);
    }, []);

    return (
        <div className="min-h-screen bg-[#030303] flex">
            {/* Sidebar overlay for mobile */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={`fixed md:sticky top-0 left-0 z-50 h-screen w-64 bg-[#080808] border-r border-white/5 flex flex-col overflow-y-auto transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
                {/* Logo */}
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setSidebarOpen(false)}>
                        <img src="/logo.png" alt="V-One Ai" className="w-9 h-9 rounded-lg object-contain" />
                        <div>
                            <div className="heading-primary text-xs text-white tracking-wider">V-One Ai</div>
                        </div>
                    </Link>
                    <button className="md:hidden p-1" onClick={() => setSidebarOpen(false)}>
                        <X className="w-5 h-5 text-white/40" />
                    </button>
                </div>

                {/* Nav sections */}
                <nav className="flex-1 px-3 py-2 space-y-1">
                    {navSections.map(section => (
                        <div key={section.title}>
                            <button onClick={() => toggleSection(section.title)} className="w-full flex items-center justify-between px-2 py-1.5 mt-2">
                                <span className="tech-mono text-[9px] text-white/20 tracking-widest">{section.title}</span>
                                <ChevronDown className={`w-3 h-3 text-white/20 transition-transform ${collapsed[section.title] ? '-rotate-90' : ''}`} />
                            </button>
                            {!collapsed[section.title] && section.items.map(item => {
                                const active = location.pathname === item.path;
                                const highlight = item.highlight;
                                return (
                                    <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                                        data-testid={`nav-${item.path.slice(1)}`}
                                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all
                                            ${highlight && !active ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/20' : ''}
                                            ${active ? 'bg-white/10 text-white border-l-2 border-cyan-400 ml-0' : !highlight ? 'text-white/40 hover:text-white/70 hover:bg-white/5' : ''}`}>
                                        <item.icon className={`w-4 h-4 ${active ? 'text-cyan-400' : highlight ? 'text-yellow-400' : ''}`} />
                                        <span>{item.label}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                {/* User */}
                <div className="p-3 border-t border-white/5">
                    <div className="flex items-center gap-3 p-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xs font-bold text-white">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs text-white truncate">{user?.name || 'User'}</div>
                            <div className="tech-mono text-[9px] text-white/30">{user?.role || 'user'}</div>
                        </div>
                        <button onClick={logout} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="logout-btn">
                            <LogOut className="w-4 h-4 text-white/30 hover:text-red-400" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-h-screen w-full">
                {/* Top bar */}
                <header className="sticky top-0 z-30 bg-[#030303]/90 backdrop-blur-xl border-b border-white/5 px-4 md:px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button className="md:hidden p-2 hover:bg-white/10 rounded-lg transition-colors" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                            <Menu className="w-5 h-5 text-white" />
                        </button>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                            <span className="tech-mono text-xs text-white/30 hidden sm:inline">SYSTEM ONLINE</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/notifications" className="relative p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="header-notification-bell">
                            <Bell className="w-4 h-4 text-white/40" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
                            )}
                        </Link>
                        <span className="tech-mono text-[10px] text-white/20 hidden sm:inline">
                            V-One Ai v3.0
                        </span>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 p-4 md:p-6 pb-20 md:pb-6">
                    {children}
                </main>
            </div>

            {/* Mobile Bottom Navigation */}
            <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-[#080808]/95 backdrop-blur-xl border-t border-white/10 safe-area-pb" data-testid="mobile-bottom-nav">
                <div className="flex justify-around items-center h-16 px-1">
                    {mobileNav.map(item => {
                        const active = location.pathname === item.path;
                        return (
                            <Link key={item.path} to={item.path}
                                data-testid={`mobile-nav-${item.path.slice(1)}`}
                                className={`flex flex-col items-center gap-0.5 py-1 px-3 rounded-lg transition-all min-w-[56px] ${active ? 'text-cyan-400' : 'text-white/30'}`}>
                                <item.icon className={`w-5 h-5 ${active ? 'text-cyan-400' : ''}`} />
                                <span className="text-[10px] font-medium">{item.label}</span>
                                {active && <div className="w-4 h-0.5 rounded-full bg-cyan-400 mt-0.5" />}
                            </Link>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
}
